import { useQuery } from "@tanstack/react-query";
import { useAuthReady } from "hooks/useAuthReady";
import { useImpersonation } from "hooks/useImpersonation/useImpersonation";
import { request } from "hooks/useOkoApi/useOkoApi";

/**
 * Fetch the current educator's account (`account/educator`) via React Query so
 * the result is cached and deduped across the app. Exposes the curriculum config
 * the lesson/standards views key off, plus a `loaded` flag that mirrors the old
 * "settled" semantics (false while the first fetch is in flight, true once it has
 * resolved or errored).
 */
/**
 * The classes belonging to the educator's currently selected school.
 *
 * The list endpoint scopes by active school in most cases, but not when an
 * educator has no active school and several accessible ones — so a multi-school
 * educator could see another school's classes in the dropdown.
 *
 * A class with no `school_id` is kept rather than hidden: the field is newer than
 * the dashboard, and dropping unknowns would empty the dropdown against an older
 * server instead of degrading to today's behaviour.
 */
export const classesInActiveSchool = (account) => {
  const all = account?.classes || [];
  const activeSchoolId = account?.active_school?.id || null;
  if (!activeSchoolId) return all;
  return all.filter((c) => !c.school_id || c.school_id === activeSchoolId);
};

export const useEducatorAccount = () => {
  const authReady = useAuthReady();
  const { impersonatedUser } = useImpersonation();
  const { data, isInitialLoading } = useQuery({
    // The request is answered as the impersonated teacher when there is one,
    // so an answer cached for one identity is never served to another.
    queryKey: ["account", "educator", impersonatedUser?.id || "self"],
    queryFn: () => request("account/educator", { method: "GET" }),
    // Nothing is asked for until there is a token to ask with.
    enabled: authReady,
  });

  return {
    account: data ?? null,
    // The school the educator is working in. It carries the standards
    // framework id the pickers key off (see utils/standards).
    activeSchool: data?.active_school ?? null,
    curriculumId: data?.config?.curriculum_id ?? null,
    curriculumKind: data?.config?.curriculum_kind ?? null,
    groupsUiVariant: data?.config?.groups_ui_variant ?? "v2",
    gradeLevels: data?.config?.grade_levels ?? [],
    loaded: authReady && !isInitialLoading,
  };
};
